import React, { useEffect, useState, useRef } from 'react';
import { 
  getCostCenters, 
  createCostCenter,
  updateCostCenter,
  addServiceToCostCenter,
  importCostCenters, 
  downloadCostCenterTemplate,
  deleteAllCostCenters, 
  deleteCostCenter 
} from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Upload, Building2, FileSpreadsheet, Trash2, AlertTriangle, Plus, Download, Edit } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import BulkImport from '../components/BulkImport';

const CostCenters = () => {
  const [costCenters, setCostCenters] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isServiceDialogOpen, setIsServiceDialogOpen] = useState(false);
  const [editingCostCenter, setEditingCostCenter] = useState(null);
  const [selectedCostCenter, setSelectedCostCenter] = useState(null);
  const fileInputRef = useRef(null);

  const [costCenterForm, setCostCenterForm] = useState({
    code: '',
    description: ''
  });

  const [serviceForm, setServiceForm] = useState({
    code: '',
    description: ''
  });

  useEffect(() => {
    fetchCostCenters();
  }, []);

  const fetchCostCenters = async () => {
    try {
      const response = await getCostCenters();
      setCostCenters(response.data);
    } catch (error) {
      toast.error('Erro ao carregar centros de custo');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const response = await importCostCenters(file);
      toast.success(response.data.message);
      fetchCostCenters();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao importar arquivo');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await downloadCostCenterTemplate();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'template_centros_custo.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Template baixado com sucesso!');
    } catch (error) {
      toast.error('Erro ao baixar template');
    }
  };

  const handleDeleteAll = async () => {
    try {
      const response = await deleteAllCostCenters();
      toast.success(response.data.message);
      fetchCostCenters();
    } catch (error) {
      toast.error('Erro ao excluir centros de custo');
    }
  };

  const handleDeleteOne = async (id) => {
    try {
      const response = await deleteCostCenter(id);
      toast.success(response.data.message);
      fetchCostCenters();
    } catch (error) {
      toast.error('Erro ao excluir centro de custo');
    }
  };

  const handleCreateCostCenter = async (e) => {
    e.preventDefault();
    try {
      if (editingCostCenter) {
        await updateCostCenter(editingCostCenter.id, costCenterForm);
        toast.success('Centro de custo atualizado com sucesso!');
      } else {
        await createCostCenter(costCenterForm);
        toast.success('Centro de custo criado com sucesso!');
      }
      setIsDialogOpen(false);
      resetCostCenterForm();
      fetchCostCenters();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar centro de custo');
    }
  };

  const handleAddService = async (e) => {
    e.preventDefault();
    try {
      await addServiceToCostCenter(selectedCostCenter.id, serviceForm);
      toast.success('Serviço adicionado com sucesso!');
      setIsServiceDialogOpen(false);
      resetServiceForm();
      fetchCostCenters();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao adicionar serviço');
    }
  };

  const resetCostCenterForm = () => {
    setCostCenterForm({ code: '', description: '' });
    setEditingCostCenter(null);
  };

  const resetServiceForm = () => {
    setServiceForm({ code: '', description: '' });
    setSelectedCostCenter(null);
  };

  const openEditDialog = (cc) => {
    setEditingCostCenter(cc);
    setCostCenterForm({ code: cc.code, description: cc.description });
    setIsDialogOpen(true);
  };

  const openAddServiceDialog = (cc) => {
    setSelectedCostCenter(cc);
    setServiceForm({ code: '', description: '' });
    setIsServiceDialogOpen(true);
  };

  return (
    <div className="space-y-6" data-testid="cost-centers-page">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-secondary font-bold uppercase tracking-tight text-slate-900">
            Centros de Custo
          </h1>
          <p className="text-slate-600 font-primary mt-1">Gerencie centros de custo e serviços</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {/* Bulk Import (novo — com preview de erros) */}
          <BulkImport entity="cost-centers" label="Centros de Custo" onDone={fetchCostCenters} />

          {/* Legacy Download Template (mantido para compat) */}
          <Button
            onClick={handleDownloadTemplate}
            variant="outline"
            className="rounded-sm"
            data-testid="download-template-button"
          >
            <Download className="h-4 w-4 mr-2" />
            Template (legado)
          </Button>

          {/* Delete All */}
          {costCenters.length > 0 && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="destructive"
                  className="rounded-sm"
                  data-testid="delete-all-button"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Apagar Todos
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-destructive" />
                    Confirmar Exclusão
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    Tem certeza que deseja excluir TODOS os centros de custo? Esta ação não pode ser desfeita.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDeleteAll} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                    Confirmar Exclusão
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}

          {/* Legacy Import Excel — mantido temporariamente */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileUpload}
            className="hidden"
            id="file-upload"
            data-testid="file-upload-input"
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            variant="outline"
            className="rounded-sm"
            data-testid="import-excel-button"
          >
            <Upload className="h-4 w-4 mr-2" />
            {uploading ? 'Importando...' : 'Importar (legado)'}
          </Button>

          {/* Add Cost Center */}
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) resetCostCenterForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm" data-testid="add-cost-center-button">
                <Plus className="h-4 w-4 mr-2" />
                Novo Centro de Custo
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-secondary uppercase tracking-tight">
                  {editingCostCenter ? 'Editar Centro de Custo' : 'Novo Centro de Custo'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateCostCenter} className="space-y-4" data-testid="cost-center-form">
                <div>
                  <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">
                    Código *
                  </Label>
                  <Input
                    value={costCenterForm.code}
                    onChange={(e) => setCostCenterForm({ ...costCenterForm, code: e.target.value })}
                    required
                    placeholder="Ex: 001"
                    className="mt-1 rounded-sm"
                    data-testid="cc-code-input"
                  />
                </div>
                <div>
                  <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">
                    Descrição *
                  </Label>
                  <Input
                    value={costCenterForm.description}
                    onChange={(e) => setCostCenterForm({ ...costCenterForm, description: e.target.value })}
                    required
                    placeholder="Ex: Estrutura"
                    className="mt-1 rounded-sm"
                    data-testid="cc-description-input"
                  />
                </div>
                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="outline" onClick={() => {
                    setIsDialogOpen(false);
                    resetCostCenterForm();
                  }}>
                    Cancelar
                  </Button>
                  <Button type="submit" className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm" data-testid="submit-cc-button">
                    {editingCostCenter ? 'Atualizar' : 'Criar'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Instructions Card */}
      <div className="bg-blue-50 border border-blue-200 rounded-sm p-4">
        <div className="flex items-start gap-3">
          <FileSpreadsheet className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
          <div>
            <h3 className="font-medium text-blue-900 mb-2">Formato do arquivo Excel</h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Coluna A: Código do centro de custo</li>
              <li>• Coluna B: Descrição do centro de custo</li>
              <li>• Coluna C: Código do serviço (formato: codigoCentro.numeroServico)</li>
              <li>• Coluna D: Descrição do serviço</li>
            </ul>
            <p className="text-sm text-blue-800 mt-2">
              💡 Clique em "Download Template" para baixar um arquivo de exemplo
            </p>
          </div>
        </div>
      </div>

      {/* Cost Centers List */}
      <div className="space-y-4">
        {costCenters.map((cc) => (
          <div
            key={cc.id}
            className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden"
            data-testid="cost-center-card"
          >
            {/* Header */}
            <div className="bg-slate-50 px-6 py-4 border-b border-slate-200">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-sm bg-accent/10">
                  <Building2 className="h-5 w-5 text-accent" strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-900">{cc.description}</h3>
                  <p className="text-sm text-slate-600">Código: {cc.code}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 rounded-sm text-xs font-medium bg-slate-100 text-slate-600">
                    {cc.services?.length || 0} serviços
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => openAddServiceDialog(cc)}
                    className="h-8 hover:bg-accent/10"
                    data-testid={`add-service-button-${cc.id}`}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Serviço
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => openEditDialog(cc)}
                    className="h-8 w-8 p-0 hover:bg-blue-50"
                    data-testid={`edit-cc-button-${cc.id}`}
                  >
                    <Edit className="h-4 w-4 text-blue-600" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0 hover:bg-destructive/10 hover:text-destructive"
                        data-testid={`delete-cc-button-${cc.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
                        <AlertDialogDescription>
                          Tem certeza que deseja excluir o centro de custo "{cc.description}"? Esta ação não pode ser desfeita.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleDeleteOne(cc.id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                          Excluir
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </div>

            {/* Services */}
            {cc.services && cc.services.length > 0 && (
              <div className="px-6 py-4">
                <div className="space-y-2">
                  {cc.services.map((service, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-3 p-3 bg-slate-50 rounded-sm"
                      data-testid="service-item"
                    >
                      <div className="flex-1">
                        <p className="font-medium text-slate-900 text-sm">{service.description}</p>
                        <p className="text-xs text-slate-600 mt-0.5">Código: {service.code}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {costCenters.length === 0 && (
        <div className="text-center py-12 bg-white border border-slate-200 rounded-sm">
          <Building2 className="h-12 w-12 text-slate-300 mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-slate-600 mb-4">Nenhum centro de custo cadastrado</p>
          <p className="text-sm text-slate-500">Importe um arquivo Excel ou crie manualmente</p>
        </div>
      )}

      {/* Add Service Dialog */}
      <Dialog open={isServiceDialogOpen} onOpenChange={(open) => {
        setIsServiceDialogOpen(open);
        if (!open) resetServiceForm();
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-secondary uppercase tracking-tight">
              Adicionar Serviço
            </DialogTitle>
            {selectedCostCenter && (
              <p className="text-sm text-slate-600">
                Centro de Custo: {selectedCostCenter.description} ({selectedCostCenter.code})
              </p>
            )}
          </DialogHeader>
          <form onSubmit={handleAddService} className="space-y-4" data-testid="service-form">
            <div>
              <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">
                Código do Serviço *
              </Label>
              <Input
                value={serviceForm.code}
                onChange={(e) => setServiceForm({ ...serviceForm, code: e.target.value })}
                required
                placeholder={selectedCostCenter ? `Ex: ${selectedCostCenter.code}.01` : 'Ex: 001.01'}
                className="mt-1 rounded-sm"
                data-testid="service-code-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                Formato: {selectedCostCenter?.code || 'codigo'}.numeroServico
              </p>
            </div>
            <div>
              <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">
                Descrição *
              </Label>
              <Input
                value={serviceForm.description}
                onChange={(e) => setServiceForm({ ...serviceForm, description: e.target.value })}
                required
                placeholder="Ex: Fundação"
                className="mt-1 rounded-sm"
                data-testid="service-description-input"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => {
                setIsServiceDialogOpen(false);
                resetServiceForm();
              }}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm" data-testid="submit-service-button">
                Adicionar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CostCenters;
